import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Plus, Building2, Search, MoreHorizontal, Globe, Palette, KeyRound, Layers, Shield } from '@/lib/icons';
import { platformApi } from '@/features/platform/api';
import { useToast } from '@/hooks/use-toast';
import {
  Card, CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/dates';

export const Route = createFileRoute('/_layout/settings/platform/tenants/')({
  component: PlatformTenantsPage,
});

interface PlatformTenant {
  id: string;
  seqId: number;
  name: string;
  status: string;
  houseBrandFallback: boolean;
  createdAt: string;
}

async function fetchPlatformTenants(limit = 50, offset = 0) {
  const resp = await platformApi.get('/api/v1/tenants', {
    params: { limit, offset },
  });
  return resp.data?.value ?? resp.data;
}

async function createPlatformTenant(data: { name: string }) {
  const resp = await platformApi.post('/api/v1/tenants', data);
  return resp.data?.value ?? resp.data;
}

function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, string> = {
    active: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
    suspended: 'bg-amber-500/15 text-amber-700 border-amber-500/30',
    deleted: 'bg-red-500/15 text-red-700 border-red-500/30',
  };
  return (
    <Badge variant="outline" className={variants[status] ?? ''}>
      {status}
    </Badge>
  );
}

function PlatformTenantsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['platform-tenants'],
    queryFn: () => fetchPlatformTenants(),
  });

  const createMutation = useMutation({
    mutationFn: createPlatformTenant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
      setDialogOpen(false);
      setNewName('');
      toast({ title: 'Tenant created', description: 'New tenant has been onboarded.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create tenant.', variant: 'destructive' });
    },
  });

  const tenants: PlatformTenant[] = Array.isArray(data) ? data : (data?.items ?? []);
  const total: number = data?.total ?? tenants.length;

  const filtered = tenants.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-heading-md tracking-tight">Platform Tenants</h1>
          <p className="text-body-sm text-muted-foreground mt-1">
            Manage tenants across the platform · {total} total
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="h-9">
              <Plus className="h-4 w-4 mr-1.5" />
              Onboard Tenant
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Onboard New Tenant</DialogTitle>
              <DialogDescription>
                Create a new tenant on the platform. You can configure branding,
                domains, and entitlements after creation.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Tenant Name</label>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Telesom Financial Services"
                  className="mt-1.5"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                disabled={!newName.trim() || createMutation.isPending}
                onClick={() => createMutation.mutate({ name: newName.trim() })}
              >
                {createMutation.isPending ? 'Creating...' : 'Create Tenant'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search tenants..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-9"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-md" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Building2 className="h-10 w-10 mb-3 opacity-40" />
              <p className="text-sm font-medium">No tenants found</p>
              <p className="text-xs mt-1">Create your first tenant to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Created</TableHead>
                  <TableHead className="hidden md:table-cell">House Brand</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((tenant) => (
                  <TableRow
                    key={tenant.id}
                    className="cursor-pointer hover:bg-accent/30"
                    onClick={() =>
                      navigate({
                        to: '/settings/platform/tenants/$tenantId',
                        params: { tenantId: tenant.id },
                      })
                    }
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary text-sm font-bold">
                          {tenant.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{tenant.name}</p>
                          <p className="text-xs text-muted-foreground font-mono">
                            {tenant.id.slice(0, 8)}…
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={tenant.status} />
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                      {formatDate(tenant.createdAt)}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {tenant.houseBrandFallback && (
                        <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 border-blue-500/30">
                          House
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate({ to: '/settings/platform/tenants/$tenantId', params: { tenantId: tenant.id } }); }}>
                            <Palette className="h-4 w-4 mr-2" />
                            Branding
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate({ to: '/settings/platform/tenants/$tenantId', params: { tenantId: tenant.id } }); }}>
                            <Globe className="h-4 w-4 mr-2" />
                            Domains
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate({ to: '/settings/platform/tenants/$tenantId', params: { tenantId: tenant.id } }); }}>
                            <Layers className="h-4 w-4 mr-2" />
                            Entitlements
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate({ to: '/settings/platform/tenants/$tenantId', params: { tenantId: tenant.id } }); }}>
                            <KeyRound className="h-4 w-4 mr-2" />
                            API Keys
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate({ to: '/settings/platform/tenants/$tenantId', params: { tenantId: tenant.id } }); }}>
                            <Shield className="h-4 w-4 mr-2" />
                            Credentials
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
